import { SurveyRequest, SurveyResponse, PersonalInfo, SurveyData } from 'src/components/GreenEnergySurvey/model/survey.model';

// API Configuration
const API_BASE_URL = 'https://gec-dev-backend.energymarketplace.in/gec-backend/api/v1';

// Survey API Controller
export class SurveyController {
  
  /**
   * Submit survey data to the API
   */
  static async submitSurvey(personalInfo: PersonalInfo, surveyData: SurveyData): Promise<SurveyResponse> {
    const requestPayload = this.mapToApiRequest(personalInfo, surveyData);
    
    try {
      const response = await fetch(`${API_BASE_URL}/energy-survey`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestPayload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: SurveyResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Survey submission error:', error);
      throw error;
    }
  }

  /**
   * Map internal survey data to API request format
   */
  static mapToApiRequest(personalInfo: PersonalInfo, surveyData: SurveyData): SurveyRequest {
    return {
      // Personal Information
      name: personalInfo.name,
      email: personalInfo.email,
      phone: personalInfo.mobile,

      // Section A: Organizational Profile
      organizationCategory: (surveyData['q1'] as string) || '',
      annualElectricityConsumption: surveyData['q2'] ? String(surveyData['q2']) : '',
      electricityProcurementSources: (surveyData['q3'] as string[]) || [],

      // Section B: Green Energy Tariff
      greenEnergyTariffAwareness: (surveyData['q4'] as string) || '',
      considerGreenEnergyTariff: (surveyData['q5'] as string) || '',
      greenEnergyTariffMotivation: (surveyData['q6'] as string[]) || [],

      // Section C: Renewable Certificates
      reCertificateAwareness: (surveyData['q7'] as string) || '',
      globalCertificateImportance: (surveyData['q8'] as string) || '',
      certificateUsage: (surveyData['q9'] as string[]) || [],

      // Section D: Scope 2 & 3 Emissions
      scope2EmissionsFamiliarity: (surveyData['q10'] as string) || '',
      scope3EmissionsFamiliarity: (surveyData['q11'] as string) || '',
      scope2StatementAgreement: (surveyData['q12'] as string) || '',
      emissionsDataRequests: (surveyData['q13'] as string) || '',

      // Section E: ESG Readiness
      currentReportingFrameworks: (surveyData['q14'] as string[]) || [],
      sustainabilityReportingChallenges: (surveyData['q15'] as string[]) || [],

      // Section F: Sustainable Finance
      sustainabilityFinanceAwareness: (surveyData['q16'] as string) || '',
      greenFinanceInfluence: (surveyData['q17'] as string) || '',
      relevantBenefits: (surveyData['q18'] as string[]) || [],

      // Section G: Transition Interest
      greenTransitionInterest: (surveyData['q19'] as string) || '',
      valuedSupport: (surveyData['q20'] as string[]) || [],

      // Optional notes field
      notes: '',
    };
  }

  /**
   * Validate personal info before submission
   */
  static validatePersonalInfo(personalInfo: PersonalInfo): { isValid: boolean; errors: Partial<PersonalInfo> } {
    const errors: Partial<PersonalInfo> = {};
    
    if (!personalInfo.name.trim()) {
      errors.name = 'Name is required';
    }
    
    // Email is optional - no validation required
    
    if (!personalInfo.mobile.trim()) {
      errors.mobile = 'Mobile number is required';
    } else if (!/^[0-9]{10}$/.test(personalInfo.mobile.replace(/\D/g, ''))) {
      errors.mobile = 'Please enter a valid 10-digit mobile number';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
}

export default SurveyController;
